import React, { useState } from 'react';
import { Calendar, FileText, Lightbulb, MessageSquare, ChevronRight, Check, Lock, Sparkles, Plus, Search } from 'lucide-react';

const EssayAreaMocks = () => {
  const [activeView, setActiveView] = useState('essays');

  return (
    <div className="min-h-screen bg-stone-50 font-['Reddit_Sans',system-ui,sans-serif]">
      {/* Top Navigation */}
      <nav className="bg-white border-b border-slate-300">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <button 
              onClick={() => setActiveView('essays')}
              className={`text-sm font-medium ${activeView === 'essays' ? 'text-slate-900' : 'text-stone-600'}`}
            >
              Essays
            </button>
            <button 
              onClick={() => setActiveView('ideas')}
              className={`text-sm font-medium ${activeView === 'ideas' ? 'text-slate-900' : 'text-stone-600'}`}
            >
              Ideas
            </button>
            <button className="text-sm font-medium text-stone-600">Timeline</button>
            <button className="text-sm font-medium text-stone-600">Colleges</button>
          </div>
          <div className="flex items-center gap-3">
            <button className="px-4 py-2 text-xs font-medium bg-gradient-to-r from-gold-400 to-gold-500 text-slate-900 rounded-lg border-2 border-slate-900 hover:scale-95 transition-transform">
              <Sparkles className="inline w-3.5 h-3.5 mr-1.5" />
              AI Essay Ideas
            </button>
            <button className="p-2 text-stone-600 hover:bg-stone-100 rounded-lg">
              <Search className="w-4 h-4" />
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeView === 'essays' && <EssaysView />}
        {activeView === 'ideas' && <IdeasView />}
      </div>
    </div>
  );
};

const EssaysView = () => {
  return (
    <div className="space-y-6">
      {/* Header with date */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-stone-600 mb-1">Thursday, October 9, 2025</p>
          <h1 className="text-xl font-bold text-slate-900">Today's Free Write</h1>
        </div>
        <button className="px-4 py-2 text-xs font-medium bg-indigo-600 text-white rounded-lg border-2 border-slate-900 hover:scale-95 transition-transform flex items-center gap-2">
          <Plus className="w-3.5 h-3.5" />
          New Essay
        </button>
      </div>

      {/* Main content area */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left sidebar */}
        <div className="lg:col-span-1 space-y-4">
          {/* Free Write section */}
          <div className="bg-white rounded-xl border border-slate-300 overflow-hidden">
            <div className="px-4 py-3 bg-slate-900/5 border-b border-slate-300">
              <h3 className="text-sm font-bold text-slate-900">Free Write</h3>
            </div>
            <div className="p-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-xs text-stone-600">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal-500"></div>
                  <span>Daily prompt available</span>
                </div>
                <button className="w-full text-left px-3 py-2 text-xs bg-stone-50 hover:bg-stone-100 rounded-lg border border-stone-300 transition-colors">
                  Get a Writing Prompt
                </button>
              </div>
            </div>
          </div>

          {/* Recent Essays */}
          <div className="bg-white rounded-xl border border-slate-300 overflow-hidden">
            <div className="px-4 py-3 bg-slate-900/5 border-b border-slate-300">
              <h3 className="text-sm font-bold text-slate-900">This Month</h3>
            </div>
            <div className="p-3 space-y-2">
              <EssayItem title="Empty entry" date="Sep 17" words="0 words" />
              <EssayItem title="Just checking to make sure..." date="Sep 10" words="13 words" status="draft" />
            </div>
          </div>

          {/* August Essays */}
          <div className="bg-white rounded-xl border border-slate-300 overflow-hidden">
            <div className="px-4 py-3 bg-slate-900/5 border-b border-slate-300">
              <h3 className="text-sm font-bold text-slate-900">August 2025</h3>
            </div>
            <div className="p-3 space-y-2">
              <EssayItem title="Empty entry" date="Aug 28" words="0 words" />
              <EssayItem title="Let's do this test..." date="Aug 26" words="12 words" />
              <EssayItem title="How about now?" date="Aug 25" words="3 words" />
            </div>
          </div>
        </div>

        {/* Main writing area */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border-2 border-teal-500 p-8 min-h-[600px]">
            <div className="mb-6 pb-4 border-b border-stone-200">
              <div className="flex items-center gap-2 text-xs text-indigo-600 mb-3">
                <Sparkles className="w-3.5 h-3.5" />
                <span className="font-medium">Get a Writing Prompt</span>
              </div>
            </div>
            
            <div className="prose prose-sm max-w-none">
              <p className="text-stone-500 text-base leading-relaxed italic">
                You don't have to finish. You just have to start.
              </p>
            </div>

            {/* Subtle progress indicator */}
            <div className="mt-8 pt-6 border-t border-stone-200">
              <div className="flex items-center justify-between text-xs text-stone-600">
                <span>0 words</span>
                <span>Auto-saved just now</span>
              </div>
            </div>
          </div>

          {/* Action cards below */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <ActionCard 
              title="Essay Builder"
              description="Guided prompts to develop your story"
              badge="RECOMMENDED"
              badgeColor="gold"
              icon={<MessageSquare className="w-4 h-4" />}
            />
            <ActionCard 
              title="Find Ideas"
              description="Explore topics through AI conversation"
              badge="DISCOVERY"
              badgeColor="indigo"
              icon={<Lightbulb className="w-4 h-4" />}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const IdeasView = () => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-slate-900 mb-1">Essay Ideas Hub</h1>
        <p className="text-xs text-stone-600">Discover compelling topics and develop unique stories for your college essays</p>
      </div>

      {/* Ideas grid */}
      <div className="grid grid-cols-2 gap-4">
        <IdeaCard
          title="Strategic Assessment Topics"
          description="Essay topics based on your assessment responses - develop stories you already have"
          badge="PERSONALIZED"
          badgeColor="azure"
          dotColor="azure"
          subtitle="AI-curated topics based on your profile"
        />
        <IdeaCard
          title="Untold Stories"
          description="Narrative opportunities found in-between-the-lines of your analysis"
          dotColor="coral"
          subtitle="Explore values and experiences that define you"
        />
        <IdeaCard
          title="Browse Categories"
          description="Explore common essay prompts organized by theme: challenges, growth, community, and more"
          badge="EXPLORE"
          badgeColor="indigo"
          dotColor="indigo"
          subtitle="Browse traditional essay themes and prompts"
        />
        <IdeaCard
          title="Hidden Competencies"
          description="AI-guided interview to uncover your unique strengths and unexpected stories"
          badge="AI INTERVIEW"
          badgeColor="teal"
          dotColor="teal"
          subtitle="Uncover hidden strengths through conversation"
        />
        <IdeaCard
          title="Character & Identity"
          description="Deep-dive interview exploring your values, experiences, and what makes you unique"
          badge="DEEP DIVE"
          badgeColor="gold"
          dotColor="gold"
          subtitle="Comprehensive exploration of your identity"
        />
        <IdeaCard
          title="Quick Brainstorm"
          description="10-minute rapid-fire session to generate essay angles you haven't considered"
          dotColor="magenta"
          subtitle="Fast idea generation for immediate inspiration"
        />
      </div>
    </div>
  );
};

const EssayItem = ({ title, date, words, status }) => (
  <button className="w-full text-left px-3 py-2 hover:bg-stone-50 rounded-lg border border-transparent hover:border-stone-300 transition-all group">
    <div className="flex items-start justify-between gap-2">
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-slate-900 truncate">{title}</p>
        <p className="text-xs text-stone-600 mt-0.5">{date} • {words}</p>
      </div>
      {status && (
        <span className="text-[10px] px-2 py-0.5 bg-stone-100 text-stone-700 rounded">
          {status}
        </span>
      )}
    </div>
  </button>
);

const ActionCard = ({ title, description, badge, badgeColor, icon }) => {
  const badgeColors = {
    gold: 'bg-gold-100 text-gold-800 border-gold-300',
    indigo: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  };

  return (
    <button className="text-left p-4 bg-white rounded-xl border border-slate-300 hover:border-slate-900 hover:shadow-sm transition-all group">
      <div className="flex items-start justify-between mb-2">
        <div className="w-8 h-8 rounded-lg bg-stone-100 border border-stone-300 flex items-center justify-center text-slate-900">
          {icon}
        </div>
        {badge && (
          <span className={`text-[10px] font-bold px-2 py-1 rounded border ${badgeColors[badgeColor]}`}>
            {badge}
          </span>
        )}
      </div>
      <h3 className="text-sm font-bold text-slate-900 mb-1">{title}</h3>
      <p className="text-xs text-stone-600 leading-relaxed">{description}</p>
      <div className="mt-3 flex items-center gap-1 text-xs text-slate-900 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
        <span>Start</span>
        <ChevronRight className="w-3 h-3" />
      </div>
    </button>
  );
};

const IdeaCard = ({ title, description, badge, badgeColor, dotColor, subtitle }) => {
  const badgeColors = {
    azure: 'bg-azure-100 text-azure-800 border-azure-300',
    indigo: 'bg-indigo-100 text-indigo-800 border-indigo-300',
    teal: 'bg-teal-100 text-teal-800 border-teal-300',
    gold: 'bg-gold-100 text-gold-800 border-gold-300',
  };

  const dotColors = {
    azure: 'bg-azure-500',
    coral: 'bg-coral-500',
    indigo: 'bg-indigo-500',
    teal: 'bg-teal-500',
    gold: 'bg-gold-400',
    magenta: 'bg-magenta-500',
  };

  return (
    <button className="text-left p-5 bg-white rounded-xl border border-slate-300 hover:border-slate-900 hover:shadow-sm transition-all group">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${dotColors[dotColor]}`}></div>
          <h3 className="text-sm font-bold text-slate-900">{title}</h3>
        </div>
        {badge && (
          <span className={`text-[10px] font-bold px-2 py-1 rounded border ${badgeColors[badgeColor]}`}>
            {badge}
          </span>
        )}
      </div>
      <p className="text-xs text-stone-700 leading-relaxed mb-3">{description}</p>
      <p className="text-xs text-stone-500 leading-relaxed">{subtitle}</p>
      <div className="mt-4 flex items-center gap-1 text-xs text-slate-900 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
        <span>Explore</span>
        <ChevronRight className="w-3 h-3" />
      </div>
    </button>
  );
};

export default EssayAreaMocks;